<template>

    <div class="pa-0 fill-height">

        <div class="pt-1" flat>

            <material-header
                :loading="loading"
                title="Landing page editor"
                info="Customize landing page theme to match your brand"
                help="subpanel-landing-page"
                icon="mdi-card-text-outline">

                <template slot="buttons">

                    <v-scale-transition>
                        <v-btn v-show="unsavedSettings || unsavedTranslations"
                               class="mr-2"
                               color="primary"
                               :loading="saving"
                               @click="saveChanges">Save changes
                        </v-btn>
                    </v-scale-transition>

                    <v-tooltip left color="primary">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" fab small class="ml-4 elevation-2" color="red" dark @click="exitClicked">
                                <v-icon> mdi-close</v-icon>
                            </v-btn>
                        </template>
                        <span>Exit editor</span>
                    </v-tooltip>
                </template>

            </material-header>

            <v-container fluid v-if="sessionActive" class="pt-0 pl-5 pr-5">

                <v-row v-if="!hasPermission(minPermission)">
                    <v-col cols="12">
                        <v-alert type="error" class="body-2">
                            Your user account doesn't have sufficient permissions for this feature
                        </v-alert>
                    </v-col>
                </v-row>

                <v-row v-if="hasPermission(minPermission)">
                    <v-col cols="3">
                        <v-scale-transition>

                            <v-expansion-panels v-model="panelsModel"
                                                v-show="!loading"
                                                tile
                                                accordion
                                                multiple>

                                <v-expansion-panel>

                                    <v-expansion-panel-header>
                                        Page selector
                                    </v-expansion-panel-header>

                                    <v-expansion-panel-content color="grey darken-2">

                                        <div v-show="!loading">

                                            <v-select
                                                v-model="landingPageModel"
                                                v-if="landingPageTypes"
                                                :items="landingPageTypes"
                                                item-text="name"
                                                item-value="type"
                                                color="grey darken-1"
                                                hint="Selected landing page"
                                                persistent-hint
                                                @change="landingPageChanged"
                                            ></v-select>

                                            <v-alert icon="mdi-alert"
                                                     type="warning"
                                                     dense
                                                     class="mt-4 mb-0 body-2"
                                                     v-show="unsavedTranslations">
                                                Changing locale will over write all textual changes made!
                                            </v-alert>

                                            <v-select
                                                v-if="subPanel"
                                                v-model="localeModel"
                                                :items="subPanel.locales"
                                                item-text="code"
                                                item-value="code"
                                                hint="Selected locale"
                                                return-object
                                                persistent-hint
                                                @change="localeChanged"
                                            ></v-select>

                                        </div>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>

                                <v-expansion-panel class="mt-2">
                                    <v-expansion-panel-header>
                                        Colors
                                    </v-expansion-panel-header>

                                    <v-expansion-panel-content color="grey darken-2">

                                        <!-- COLOR PICKERS -->
                                        <color-picker key="gradientStart"
                                                      name="Gradient start"
                                                      color="grey darken-2"
                                                      v-model="settings.gradientStart"
                                                      @change="unsavedSettings=true"
                                        ></color-picker>

                                        <color-picker key="gradientEnd"
                                                      name="Gradient end"
                                                      color="grey darken-2"
                                                      v-model="settings.gradientEnd"
                                                      @change="unsavedSettings=true"
                                        ></color-picker>

                                        <color-picker key="foregroundColor"
                                                      name="Content box"
                                                      color="grey darken-2"
                                                      v-model="settings.foregroundColor"
                                                      @change="unsavedSettings=true"
                                        ></color-picker>

                                        <color-picker key="actionButtonColor"
                                                      name="Action button"
                                                      v-model="settings.actionButtonColor"
                                                      @change="unsavedSettings=true"
                                                      color="grey darken-2"
                                        ></color-picker>

                                        <color-picker key="communityButtonColor"
                                                      name="Community button"
                                                      v-model="settings.communityButtonColor"
                                                      @change="unsavedSettings=true"
                                                      color="grey darken-2"
                                        ></color-picker>

                                        <color-picker key="profileButtonColor"
                                                      name="Profile button"
                                                      color="grey darken-2"
                                                      v-model="settings.profileButtonColor"
                                                      @change="unsavedSettings=true"
                                        ></color-picker>

                                        <color-picker name="Reward box color"
                                                      color="grey darken-2"
                                                      v-model="settings.rewardBoxColor"
                                                      @change="unsavedSettings=true"
                                        ></color-picker>

                                        <color-picker key="cookieNoticeColor"
                                                      name="Cookie notice color"
                                                      color="grey darken-2"
                                                      v-model="settings.cookieNoticeColor"
                                                      @change="unsavedSettings=true"
                                        ></color-picker>

                                        <color-picker key="surveyBoxColor"
                                                      name="Survey box color"
                                                      color="grey darken-2"
                                                      v-model="settings.surveyBoxColor"
                                                      @change="unsavedSettings=true"
                                        ></color-picker>

                                    </v-expansion-panel-content>
                                </v-expansion-panel>

                                <v-expansion-panel class="mt-2">
                                    <v-expansion-panel-header>
                                        Settings
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content color="grey darken-2">

                                        <v-select v-model="settings.logoAlignment"
                                                  :items="logoAlignments"
                                                  hint="Logo alignment"
                                                  persistent-hint
                                                  item-value="value"
                                                  item-text="name"
                                                  @change="unsavedSettings=true"
                                        ></v-select>

                                        <v-slider
                                            class="mt-7"
                                            v-model="settings.logoSize"
                                            thumb-color="primary"
                                            :thumb-label="true"
                                            thumb-size="30"
                                            persistent-hint
                                            hint="Logo size"
                                            min="100"
                                            max="900"
                                            step="10"
                                            dense
                                            @change="unsavedSettings=true"
                                        ></v-slider>

                                        <v-slider
                                            class="mt-7"
                                            v-model="settings.gradientAngle"
                                            thumb-color="primary"
                                            :thumb-label="true"
                                            thumb-size="30"
                                            hint="Gradient angle"
                                            persistent-hint
                                            min="0"
                                            max="360"
                                            step="10"
                                            dense
                                            @change="unsavedSettings=true"
                                        ></v-slider>

                                        <v-slider
                                            class="mt-7"
                                            v-model="settings.elevation"
                                            thumb-color="primary"
                                            :thumb-label="true"
                                            thumb-size="30"
                                            hint="Content box elevation"
                                            persistent-hint
                                            min="0"
                                            max="24"
                                            step="1"
                                            dense
                                            @change="unsavedSettings=true"
                                        ></v-slider>

                                    </v-expansion-panel-content>
                                </v-expansion-panel>


                                <v-expansion-panel class="mt-2">
                                    <v-expansion-panel-header>
                                        Elements
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content color="grey darken-2">

                                        <v-switch hide-details
                                                  v-model="settings.showLogo"
                                                  color="primary"
                                                  label="Logo"
                                                  @change="unsavedSettings=true"
                                        ></v-switch>

                                        <v-switch hide-details
                                                  v-model="settings.showCommunityButton"
                                                  color="primary"
                                                  label="Community button"
                                                  @change="unsavedSettings=true"
                                        ></v-switch>

                                        <v-switch hide-details
                                                  v-model="settings.showSupportButton"
                                                  color="primary"
                                                  label="Support button"
                                                  class="mb-5"
                                                  @change="unsavedSettings=true"
                                        ></v-switch>

                                    </v-expansion-panel-content>
                                </v-expansion-panel>

                            </v-expansion-panels>

                        </v-scale-transition>

                    </v-col>

                    <!--- BROWSER PREVIEW -->
                    <v-col :cols="9">
                        <v-fade-transition>
                            <title-card title="Browser preview" no-padding v-show="!loading" :loading="loadingLocale">

                                <v-scale-transition>

                                    <landing-page
                                        v-if="!loading"
                                        :type="landingPageModel"
                                        :logo="logo"
                                        :edit-mode="true"
                                        :sub-panel-id="subPanel.id"
                                        :locale="localeModel ? localeModel : ''"
                                        :community-url="subPanel && subPanel.url"
                                        :support-email="subPanel && subPanel.support_email"
                                        :sub-panel-name="subPanel.name"
                                        :rewards-earned="rewardsEarned"
                                        :translations="translations"
                                        :sub-panel-translations="subPanelTranslations"
                                        :settings="settings"
                                        :component-data="componentData"
                                        @change:subPanelTranslations="updateSubPanelTranslations"
                                        @change:translations="updateTranslations"
                                    ></landing-page>

                                </v-scale-transition>

                            </title-card>
                        </v-fade-transition>
                    </v-col>
                </v-row>

                <!-- CONFIRMATION DIALOG FOR UNSAVED CHANGES -->
                <confirmation-dialog v-model="warningDialogModel"
                                     headline="WARNING: You have unsaved changes!"
                                     description="You have unsaved changes! Make sure translations and/or settings are saved for this template. Changes will be lost if you exit!"
                                     confirm-button-text="Exit"
                                     abort-button-text="Abort"
                                     @selected="confirmDialogSelected">
                </confirmation-dialog>

            </v-container>

            <v-card v-if="debug">
                <json :data="landingPages"></json>
            </v-card>
        </div>

    </div>

</template>

<script>

import {mapActions, mapGetters} from 'vuex';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import LandingPage from "../../../components/UI/LandingPage/index";
import Json from 'vue-json-pretty';
import ColorPicker from "../../../components/UI/ColorPicker";
import TitleCard from "../../../components/UI/TitleCard";
import SubPanel from "../ts/SubPanel";
import ConfirmationDialog from "../../../components/UI/ConfirmationDialog";

export default {
    mixins: [activeSessionChecker],

    components: {
        ConfirmationDialog,
        MaterialHeader,
        LandingPage,
        Json,
        ColorPicker,
        TitleCard
    },

    props: {
        subPanel: {
            required: true,
            type: SubPanel,
        }
    },

    data() {
        return {
            debug: false,
            loading: true,
            loadingLocale: false,

            minPermission: 'update sub panels',
            landingPages: null,
            landingPageModel: null,
            localeModel: null,

            backgroundPickerModel: false,
            foregroundPickerModel: false,
            communityButtonColorPickerModel: false,
            profileButtonColorPickerModel: false,
            buttonTextColorPickerModel: false,
            previewSettingsMenuModel: false,

            unsavedTranslations: false,
            unsavedSettings: false,
            translationsSaving: false,

            settings: {
                gradientStart: '#FFFFFF',
                gradientEnd: '#CCCCCC',
                gradientAngle: 0,

                foregroundColor: '#FFFFFF',

                logoSize: 900,
                logoAlignment: 'center',

                elevation: 10,

                showLogo: true,
                showCommunityButton: true,
                showSupportButton: true,

                actionButtonColor: '#329EF4',
                profileButtonColor: '#329EF4',
                communityButtonColor: '#329EF4',
                surveyBoxColor: '#EEEEEE',
                rewardBoxColor: '#43A047',
                cookieNoticeColor: '#329EF4',
            },

            translations: {
                title: null,
                content: null,
            },

            subPanelTranslations: {
                rewards_earned: null,
                rewards_earned_text: null,
                rating_question_text: null,
                rating_thank_you_text: null,
                community_button_text: null,
                profile_button_text: null,
                support_button_text: null,
            },

            logoAlignments: [
                {name: "Left", value: "left"},
                {name: "Center", value: "center"},
                {name: "Right", value: "right"},
            ],

            settingsSaving: false,
            panelsModel: [0,1],
            warningDialogModel: false,
        }
    },

    computed: {
        ...mapGetters('SubPanels', [
            'findById'
        ]),

        // Fake component data
        componentData() {

            if (this.landingPageModel === 'completed') {

                return {
                    reward: {
                        points: 100,
                    },
                    surveyRating: {
                        enabled: true,
                        rating: null,
                    },
                    dynamicProfiling: {
                        enabled: true,
                        max_questions: 3,
                    }
                }
            }

            if (this.landingPageModel === 'project-entry') {

                return {
                    projectInfo: {
                        welcome_enabled: true
                    }
                }
            }

            // Return empty component data
            let data = {};
            data[this.landingPageModel] = {}

            return data;
        },

        logo() {
            if (this.subPanel && this.subPanel.logo_url) {
                return this.subPanel.logo_url;
            } else {
                return null;
            }
        },

        landingPageTypes() {
            if (this.landingPages) {

                return this.landingPages.map(landingPage => {
                    return {
                        name: landingPage.name,
                        type: landingPage.type
                    }
                }).sort((a, b) => {

                    if (a.name < b.name) {
                        return -1;
                    }
                    if (a.name > b.name) {
                        return 1;
                    }

                    // names must be equal
                    return 0;
                });
            }
        },

        pageTitle() {
            if (this.landingPages && this.landingPageModel) {
                this.findLandingPage(this.landingPageModel);
            }
        },

        surveyRatingsDisabled() {
            return !this.$store.state.PanelSettings.settings.survey_ratings_enabled;
        },

        dynamicProfilingDisabled() {
            return !this.$store.state.PanelSettings.settings.dynamic_profiling_enabled;
        },

        rewardsEarned() {
            return this.landingPageModel === 'completed' ? 100 : 10;
        },

        saving() {
            return this.settingsSaving || this.translationsSaving;
        }
    },

    mounted() {

        this.settings = Object.assign(this.settings, this.subPanel.landing_page_settings);
        this.localeModel = this.subPanel.locales[0];

        // This is here in case user reloads the page
        this.loading = true;

        this.getLandingPages({
            id: this.subPanel.id,
            locale: this.localeModel.code
        });
    },

    methods: {
        ...mapActions('SubPanels', [
            'loadSubPanels',
            'loadLandingPages',
            'saveLandingPageSettings',
            'saveLandingPageTranslations'
        ]),

        saveChanges() {
            if (this.unsavedSettings) this.saveSettings();
            if (this.unsavedTranslations) this.saveTranslations();
        },

        saveSettings() {

            this.settingsSaving = true;

            this.saveLandingPageSettings({
                subPanel: this.subPanel,
                settings: this.settings
            })
                .then(() => {
                    Bus.$emit('flash-message', {message: 'Changes saved...'});
                })
                .catch(() => {
                    Bus.$emit('flash-message', {type: 'error', message: 'OOPS... Settings failed to save!'});
                })
                .finally(() => {
                    this.settingsSaving = false;
                    this.unsavedSettings = false;
                });
        },

        saveTranslations() {

            this.translationsSaving = true;

            this.saveLandingPageTranslations({
                id: this.subPanel.id,
                locale: this.localeModel.code,
                data: {
                    type: this.landingPageModel,
                    translations: this.translations,
                    subPanelTranslations: this.subPanelTranslations
                }
            })
                .then(response => {
                    this.unsavedTranslations = false;
                    Bus.$emit('flash-message', {message: 'Changes saved...'});
                })
                .catch(error => {
                    Bus.$emit('flash-message', {type: 'error', message: 'OOPS we failed to save translations...'});
                })
                .finally(() => {
                    this.translationsSaving = false
                    this.unsavedTranslations = false;
                });
        },

        getLandingPages(config) {
            this.loading = true;
            this.loadLandingPages(config)
                .then(response => {
                    this.landingPages = response.data.landingPages;
                    this.subPanelTranslations = response.data.subPanelTranslations;
                    this.landingPageChanged('completed');
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        landingPageChanged(type) {
            const landingPage = this.findLandingPage(type);
            if (!landingPage) throw new Error("Landing page could not be found!");

            this.translations = landingPage.translations;
            this.landingPageModel = type;
        },

        localeChanged(locale) {

            this.loadingLocale = true;

            this.loadLandingPages({
                id: this.subPanel.id,
                locale: locale.code
            })
                .then(response => {
                    this.landingPages = response.data.landingPages;
                    this.subPanelTranslations = response.data.subPanelTranslations;
                    this.landingPageChanged(this.landingPageModel);

                })
                .finally(() => {
                    this.loadingLocale = false;
                });
        },

        findLandingPage(type) {
            return this.landingPages.find(l => l.type === type);
        },

        updateTranslations(translations) {
            this.unsavedTranslations = true;
            this.translations = translations;
        },

        updateSubPanelTranslations(translations) {
            this.unsavedTranslations = true;
            this.subPanelTranslations = translations;
        },

        exitClicked() {
            if (this.unsavedSettings || this.unsavedTranslations) {
                this.warningDialogModel = true;
            } else {
                this.$emit('close');
            }
        },

        confirmDialogSelected(exit) {
            if (exit) {
                this.$emit('close');
            }
        }
    }
}
</script>
